import { useCallback, useMemo, useState } from "react";
import { DialogProps } from "../misc/DialogProps";
import { useLocation } from "react-router-dom";
import { parse as parseQueryString } from "qs";
import { v4 as randomUuid } from "uuid";

export function useDialog(dialogName?: string): {
  dialogProps: DialogProps;
  isDialogOpen: boolean;
  openDialog: () => void;
  closeDialog: () => void;
} {
  const location = useLocation();
  const { openDialog: openDialogName } = parseQueryString(location.search, { ignoreQueryPrefix: true });
  const [counter, setCounter] = useState(dialogName && dialogName === openDialogName ? 1 : 0);
  const keyPrefix = useMemo(() => dialogName || randomUuid(), []);
  // Return a new key whenever the dialog is closed, to ensure
  // that no state of a previously opened dialog is reused ...
  const key = `${keyPrefix}-${counter}`;
  const open = counter % 2 === 1;
  const openDialog = useCallback(() => {
    if (dialogName) {
      const url = new URL(window.location.href);
      url.searchParams.set("openDialog", dialogName);
      browserHistory.replace(url.toString());
    }
    setCounter((value) => {
      if (value % 2 === 1) {
        // The dialog is already open.
        return value;
      }
      return value + 1;
    });
  }, [dialogName]);
  const closeDialog = useCallback(() => {
    if (dialogName) {
      const url = new URL(window.location.href);
      url.searchParams.delete("openDialog");
      browserHistory.replace(url.toString());
    }
    setCounter((value) => {
      if (value % 2 === 0) {
        // The dialog is already closed.
        return value;
      }
      return value + 1;
    });
  }, [dialogName]);
  const dialogProps: DialogProps = { key, open, onClose: closeDialog };

  return { dialogProps, isDialogOpen: open, openDialog, closeDialog };
}
